import type {ValuationData} from "@reva/shared/valuation-data";

import type ServiceApi from "../../api";
import {RevaApi} from "../api-constants";
import {SimpleApiInterface} from "../api-interface";
import type {ApiCredentials} from "../credentials";
import type {ApiExecutor} from "../executor";

/**
 * Auftragsart, die sich an eine Bewertung hängen bzw. von ihr lösen lässt - zugleich das URL-Segment
 * hinter {@link RevaApi.PATH_VALUATION_VALUE_ORDERS}. Die Zuordnung aus dem fachlichen
 * `ValueOrderType` macht `valueOrdersThunk`.
 */
export type OrderTypeAttachable = "inspection";

/**
 * Gemeinsame Basis für das An- und Abhängen eines ValueAG-Auftrags
 * ({@link RevaApi.PATH_VALUATIONS}/{id}/{@link RevaApi.PATH_VALUATION_VALUE_ORDERS}/{orderType}[/…]).
 *
 * Beide Endpunkte antworten mit der Bewertung wie ein Laden. Ist ein Snapshot geladen, sagt
 * `snapshot` dem Backend, die Antwort als diesen Snapshot zu rahmen, statt den Client auf den
 * Live-Stand umzuschalten; ohne Snapshot entfällt der Query-Parameter.
 */
abstract class ValueOrderAttachmentApiInterface extends SimpleApiInterface<ValuationData> {
    protected constructor(
        protected readonly revaId: string,
        protected readonly orderType: OrderTypeAttachable,
        protected readonly snapshotNumber: number | null,
    ) {
        super();
    }

    /** Pfadsegmente hinter der Auftragsart; beim Anhängen keine. */
    protected orderPathSegments(): string[] {
        return [];
    }

    protected buildUrl(api: ServiceApi): string {
        return api.buildRevaApiUrl(
            [
                RevaApi.PATH_VALUATIONS,
                this.revaId,
                RevaApi.PATH_VALUATION_VALUE_ORDERS,
                this.orderType,
                ...this.orderPathSegments(),
            ],
            {snapshot: this.snapshotNumber},
        );
    }
}

/**
 * Hängt einen bestehenden ValueAG-Auftrag über seine externe Auftragsnummer an die Bewertung
 * (POST …/{@link RevaApi.PATH_VALUATION_VALUE_ORDERS}/{orderType}).
 */
export class AttachValueOrderApiInterface extends ValueOrderAttachmentApiInterface {
    constructor(
        revaId: string,
        private readonly externalOrderNumber: string,
        orderType: OrderTypeAttachable,
        snapshotNumber: number | null = null,
    ) {
        super(revaId, orderType, snapshotNumber);
    }

    protected buildPayload(): object {
        return {valueExternalOrderNumber: this.externalOrderNumber};
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchPOST(url, credentials, this.buildPayload());
    }
}

/**
 * Löst einen ValueAG-Auftrag wieder von der Bewertung
 * (DELETE …/{@link RevaApi.PATH_VALUATION_VALUE_ORDERS}/{orderType}/{valueOrderGuid}).
 */
export class DetachValueOrderApiInterface extends ValueOrderAttachmentApiInterface {
    constructor(
        revaId: string,
        private readonly valueOrderGuid: string,
        orderType: OrderTypeAttachable,
        snapshotNumber: number | null = null,
    ) {
        super(revaId, orderType, snapshotNumber);
    }

    protected orderPathSegments(): string[] {
        return [this.valueOrderGuid];
    }

    protected async executeInternal(
        executor: ApiExecutor,
        url: string,
        credentials: ApiCredentials,
    ): Promise<ValuationData> {
        return executor.fetchDELETE(url, credentials);
    }
}
